import { ref, push, set, update, remove, onValue } from 'firebase/database'
import { db, tref } from './firebase.js'

// Pro Shop Credit ledger — a manual, shop-controlled store of credit accounts
// and gift cards. Persisted under /club/proShopCredit (the /club node is
// already writable in the RTDB rules, so this needs no rules change).
//   accounts/{id}: { name, createdAt, transactions: { txId: { type, amount, reason, date, createdAt } } }
//   giftCards/{id}: { code, value, recipient, createdAt, redemptions: { redId: { amount, note, date, createdAt } } }
// Balances are never stored — always recomputed from transactions/redemptions,
// so editing or deleting an entry recalculates automatically.
const BASE = 'club/proShopCredit'

export function watchProShopCredit(cb) {
  return onValue(tref(BASE), (snap) => cb(snap.val() || {}))
}

// ── Accounts ─────────────────────────────────────────────────────────────
export async function createAccount(name) {
  const clean = String(name || '').trim()
  if (!clean) throw new Error('Account name is required.')
  const node = push(tref(`${BASE}/accounts`))
  await set(node, { name: clean, createdAt: Date.now() })
  return node.key
}

export async function deleteAccount(accountId) {
  await remove(tref(`${BASE}/accounts/${accountId}`))
}

// ── Credit transactions ──────────────────────────────────────────────────
// type: 'credit' (adds) | 'redeem' (deducts). Reason required for credit so
// the shop can always see WHY credit was issued.
export async function addTransaction(accountId, { type, amount, reason, date }) {
  const amt = Number(amount)
  if (!Number.isFinite(amt) || amt <= 0) throw new Error('Enter a valid amount.')
  const kind = type === 'redeem' ? 'redeem' : 'credit'
  const why = String(reason || '').trim()
  if (kind === 'credit' && !why) throw new Error('A reason is required when adding credit.')
  const node = push(tref(`${BASE}/accounts/${accountId}/transactions`))
  await set(node, {
    type: kind,
    amount: amt,
    reason: why,
    date: date || new Date().toISOString().slice(0, 10),
    createdAt: Date.now(),
  })
  return node.key
}

export async function updateTransaction(accountId, txId, patch) {
  const clean = {}
  if (patch.amount != null) {
    const amt = Number(patch.amount)
    if (!Number.isFinite(amt) || amt <= 0) throw new Error('Enter a valid amount.')
    clean.amount = amt
  }
  if (patch.reason != null) clean.reason = String(patch.reason).trim()
  if (patch.date != null) clean.date = patch.date
  if (patch.type != null) clean.type = patch.type === 'redeem' ? 'redeem' : 'credit'
  await update(tref(`${BASE}/accounts/${accountId}/transactions/${txId}`), clean)
}

export async function deleteTransaction(accountId, txId) {
  await remove(tref(`${BASE}/accounts/${accountId}/transactions/${txId}`))
}

// ── Gift cards / certificates ────────────────────────────────────────────
export async function createGiftCard({ code, value, recipient }) {
  const c = String(code || '').trim()
  const v = Number(value)
  if (!c) throw new Error('A code is required.')
  if (!Number.isFinite(v) || v <= 0) throw new Error('Enter a valid value.')
  const node = push(tref(`${BASE}/giftCards`))
  await set(node, {
    code: c,
    value: v,
    recipient: String(recipient || '').trim() || null,
    createdAt: Date.now(),
  })
  return node.key
}

export async function addGiftCardRedemption(cardId, { amount, note, date }) {
  const amt = Number(amount)
  if (!Number.isFinite(amt) || amt <= 0) throw new Error('Enter a valid amount.')
  const node = push(tref(`${BASE}/giftCards/${cardId}/redemptions`))
  await set(node, {
    amount: amt,
    note: String(note || '').trim(),
    date: date || new Date().toISOString().slice(0, 10),
    createdAt: Date.now(),
  })
  return node.key
}

export async function deleteGiftCardRedemption(cardId, redId) {
  await remove(tref(`${BASE}/giftCards/${cardId}/redemptions/${redId}`))
}

export async function deleteGiftCard(cardId) {
  await remove(tref(`${BASE}/giftCards/${cardId}`))
}
